import { IntegrationsService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { SpotToast } from "../../SpotToast";
import { useQuery } from "@tanstack/react-query";
import { ISharepointInfo } from "./";
import { NoxusFormLabel } from "../NoxusFormLabel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";

export const SharepointSitePicker = ({
  activeSharepoint,
  setActiveSharepoint,
  credentialId,
}: {
  activeSharepoint: ISharepointInfo | undefined;
  setActiveSharepoint: (site: ISharepointInfo) => void;
  credentialId?: string;
}) => {
  const { spotGroup } = useSpotUser();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["sharepoint-sites", credentialId],
    queryFn: () =>
      IntegrationsService.getSharepointSites({
        path: { group_id: spotGroup.id },
      }),
  });

  const rootSiteId = activeSharepoint?.site?.id;
  const { data: subsitesData } = useQuery({
    queryKey: ["sharepoint-subsites", rootSiteId, credentialId],
    queryFn: () =>
      IntegrationsService.getSharepointSubsites({
        path: { group_id: spotGroup.id, site_id: rootSiteId || "" },
      }),
    enabled: !!rootSiteId,
  });

  const effectiveSite = activeSharepoint?.subsite ?? activeSharepoint?.site;
  const { data: drivesData, isLoading: drivesLoading } = useQuery({
    queryKey: ["sharepoint-drive-items", effectiveSite?.id, credentialId],
    queryFn: () =>
      IntegrationsService.getSharepointSiteDrives({
        path: {
          group_id: spotGroup.id,
          site_id: effectiveSite?.id || "",
        },
      }),
    enabled: !!effectiveSite?.id,
  });

  const sites = data?.data ?? [];
  const subsites = subsitesData?.data ?? [];
  const drives = drivesData?.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div>
        <NoxusFormLabel label="Site" isRequired />
        <Select
          value={activeSharepoint?.site?.id ?? ""}
          onValueChange={(val) => {
            const site = sites.find((s) => s.id === val);
            if (site) {
              setActiveSharepoint({
                site,
                subsite: undefined,
                drive: undefined,
              });
            } else {
              SpotToast.error("Failed to select sharepoint site");
            }
          }}
          disabled={isLoading}
        >
          <SelectTrigger className="w-full h-8 text-xs bg-background">
            <SelectValue placeholder="Select a site…" />
          </SelectTrigger>
          <SelectContent>
            {isError ? (
              <div className="py-2 px-3 text-xs text-destructive">
                Failed to load SharePoint sites: check the connected credentials
                and try again
              </div>
            ) : sites.length === 0 ? (
              <div className="py-2 px-3 text-xs text-muted-foreground">
                No sites available for connected credentials
              </div>
            ) : (
              sites.map((site) => (
                <SelectItem key={site.id} value={site.id} className="text-xs">
                  {site.displayName}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {activeSharepoint?.site?.id && subsites.length > 0 && (
        <div>
          <NoxusFormLabel label="Subsite" />
          <Select
            value={activeSharepoint?.subsite?.id ?? activeSharepoint.site.id}
            onValueChange={(val) => {
              const site = activeSharepoint.site;
              const sub = subsites.find((s) => s.id === val);
              setActiveSharepoint({ site, subsite: sub, drive: undefined });
            }}
          >
            <SelectTrigger className="w-full h-8 text-xs bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={activeSharepoint.site.id} className="text-xs">
                This site
              </SelectItem>
              {subsites.map((subsite) => (
                <SelectItem
                  key={subsite.id}
                  value={subsite.id}
                  className="text-xs"
                >
                  {subsite.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {activeSharepoint?.site?.id && (
        <div>
          <NoxusFormLabel label="Source" isRequired />
          <Select
            value={activeSharepoint?.drive?.id ?? ""}
            onValueChange={(val) => {
              const drive = drives.find((d) => d.id === val);
              if (drive) {
                setActiveSharepoint({
                  ...activeSharepoint,
                  drive: { id: drive.id, name: drive.name },
                });
              }
            }}
            disabled={drivesLoading || !activeSharepoint?.site?.id}
          >
            <SelectTrigger className="w-full h-8 text-xs bg-background">
              <SelectValue placeholder="Select a source…" />
            </SelectTrigger>
            <SelectContent>
              {drives.length === 0 ? (
                <div className="py-2 px-3 text-xs text-muted-foreground">
                  No sources available
                </div>
              ) : (
                drives.map((drive) => (
                  <SelectItem
                    key={drive.id}
                    value={drive.id}
                    className="text-xs"
                  >
                    {drive.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};
